import type { RequestInterface } from '~/common/types';
import {
  Company,
  CompanyRequest,
  CreateCompanyTokenRequestParams,
  validateSSOSettingsRequestParams,
  ssoSettingsRequestParams,
} from '~/companies/types';
import { Settings } from '~/features/types';
export class CompaniesRequest implements RequestInterface {
  method = 'get';
  path = '/api/v1/companies';
  headers: Record<string, any>;
  params?: Record<string, any>;

  constructor({
    token,
    params,
  }: {
    token: string;
    params?: Record<string, any>;
  }) {
    this.headers = {
      authorization: `Bearer ${token}`,
    };
    if (params) {
      this.params = {
        ...params,
      };
    }
  }
}

export class GetCompanyRequest implements RequestInterface {
  method = 'get';
  path: string;
  headers: Record<string, any>;
  params?: Record<string, any>;

  constructor({
    token,
    companyId,
    params,
  }: {
    token: string;
    companyId: string;
    params?: Record<string, any>;
  }) {
    this.path = `/api/v1/companies/${companyId}`;
    this.headers = {
      authorization: `Bearer ${token}`,
    };
    if (params) {
      this.params = {
        ...params,
      };
    }
  }
}

export class UpdateCompanyRequest implements RequestInterface {
  method = 'patch';
  path: string;
  headers: Record<string, any>;
  data: Record<string, any>;
  params?: Record<string, any>;

  constructor({
    token,
    companyId,
    company,
    params,
  }: {
    token: string;
    companyId: string;
    company: CompanyRequest;
    params?: Record<string, any>;
  }) {
    this.path = `/api/v1/companies/${companyId}`;
    this.headers = {
      authorization: `Bearer ${token}`,
    };
    this.data = {
      ...company,
    };
    if (params) {
      this.params = {
        ...params,
      };
    }
  }
}

export class UpdateCompanySettingsRequest implements RequestInterface {
  method = 'patch';
  path: string;
  headers: Record<string, any>;
  data: Record<string, any>;
  params?: Record<string, any>;

  constructor({
    token,
    companyId,
    featureId,
    settings,
    params,
  }: {
    token: string;
    companyId: string;
    featureId: string;
    settings: Settings;
    params?: Record<string, any>;
  }) {
    this.path = `/api/v1/companies/${companyId}/feature/${featureId}/settings`;
    this.headers = {
      authorization: `Bearer ${token}`,
    };
    this.data = {
      ...settings,
    };
    if (params) {
      this.params = {
        ...params,
      };
    }
  }
}

export class CreateCompanyRequest implements RequestInterface {
  method = 'post';
  path = '/api/v1/companies';
  headers: Record<string, any>;
  data: Record<string, any>;

  constructor({ token, company }: { token: string; company: Company }) {
    this.headers = {
      authorization: `Bearer ${token}`,
    };
    this.data = {
      ...company,
    };
  }
}

export class GetCompanyTokenRequest implements RequestInterface {
  method = 'get';
  path: string;
  headers: Record<string, any>;

  constructor({ token, company }: { token: string; company: string }) {
    this.headers = {
      authorization: `Bearer ${token}`,
    };

    this.path = `/api/v1/company-tokens?entity_id=${company}`;
  }
}

export class DeleteCompanyTokenRequest implements RequestInterface {
  method = 'delete';
  path: string;
  headers: Record<string, any>;
  data: Record<string, any> = {};

  constructor({ token, company }: { token: string; company: string }) {
    this.headers = {
      authorization: `Bearer ${token}`,
    };

    this.path = `/api/v1/company-tokens/${company}/revoke`;
  }
}
export class RefreshCompanyTokenRequest implements RequestInterface {
  method = 'post';
  path: string;
  headers: Record<string, any>;
  data: Record<string, any> = {};

  constructor({
    token,
    company,
    tokenID,
  }: {
    token: string;
    company: string;
    tokenID;
  }) {
    this.headers = {
      authorization: `Bearer ${token}`,
    };
    this.data = { entity_id: company };
    this.path = `/api/v1/company-tokens/${tokenID}/rotate`;
  }
}

export class CreateCompanyTokenRequest implements RequestInterface {
  method = 'post';
  path = '/api/v1/company-tokens';
  headers: Record<string, any>;
  data: Record<string, any>;

  constructor({ token, companyToken }: CreateCompanyTokenRequestParams) {
    this.headers = {
      authorization: `Bearer ${token}`,
    };

    this.data = {
      ...companyToken,
    };
  }
}

export class validateSSOSettingsRequest implements RequestInterface {
  method = 'post';
  path = '/api/v1/validate-sso-credentials';
  headers: Record<string, any>;
  data: Record<string, any>;
  constructor({ token, SsoDetail }: validateSSOSettingsRequestParams) {
    this.headers = {
      authorization: `Bearer ${token}`,
    };
    this.data = {
      ...SsoDetail,
    };
  }
}

export class saveSSOSettingsRequest implements RequestInterface {
  method = 'post';
  path = '/api/v1/sso-credentials';
  headers: Record<string, any>;
  data: Record<string, any>;
  constructor({ token, SsoDetail }: ssoSettingsRequestParams) {
    this.headers = {
      authorization: `Bearer ${token}`,
    };
    this.data = {
      ...SsoDetail,
    };
  }
}

export class getSSOSettingsRequest implements RequestInterface {
  method = 'get';
  path = '/api/v1/sso-credentials';
  headers: Record<string, any>;
  params?: Record<string, any>;

  constructor({
    token,
    company,
    provider,
    include,
  }: {
    token: string;
    company: string;
    provider: string;
    include?: string;
  }) {
    this.headers = {
      authorization: `Bearer ${token}`,
    };
    this.params = {
      company_id: company,
      provider,
      ...(include ? { include } : {}),
    };
  }
}

export class loadOktaGroupsRequest implements RequestInterface {
  method = 'get';
  path: string;
  headers: Record<string, any>;

  constructor({ token, company }: { token: string; company: string }) {
    this.headers = {
      authorization: `Bearer ${token}`,
    };
    this.path = `/api/v1/okta-groups?company_id=${company}`;
  }
}
export class UpdateSsoRoleSettingsRequest implements RequestInterface {
  method = 'post';
  path = '/api/v1/sso-role-settings';
  headers: Record<string, any>;
  data: Record<string, any>;

  constructor({
    token,
    company,
    default_role,
    payload,
  }: {
    token: string;
    company: string;
    default_role: string;
    payload: any;
  }) {
    this.headers = {
      authorization: `Bearer ${token}`,
    };
    this.data = {
      company_id: company,
      default_role,
      mappings: payload,
    };
  }
}

export class fetchOktaRoleRequest implements RequestInterface {
  method = 'get';
  path: string;
  headers: Record<string, any>;

  constructor({ token, company }: { token: string; company: string }) {
    this.headers = {
      authorization: `Bearer ${token}`,
    };
    this.path = `/api/v1/fetch-okta-roles?company_id=${company}`;
  }
}
export class getSSOMappingRequest implements RequestInterface {
  method = 'get';
  path: string;
  headers: Record<string, any>;

  constructor({ token, company }: { token: string; company: string }) {
    this.headers = {
      authorization: `Bearer ${token}`,
    };
    this.path = `/api/v1/get-sso-mapping?company_id=${company}`;
  }
}
export class getDefaultRoleRequest implements RequestInterface {
  method = 'get';
  path: string;
  headers: Record<string, any>;

  constructor({ token, company }: { token: string; company: string }) {
    this.headers = {
      authorization: `Bearer ${token}`,
    };
    this.path = `/api/v1/get-default-role?company_id=${company}`;
  }
}
